// Focus + scroll to the first errored field on a failed submit.
//
// Call this on the failure branch of a submit handler (after the form has flipped its
// "submitted"/reveal-errors state) with the form/dialog body element. It runs inside a
// requestAnimationFrame so the error UI (aria-invalid / data-invalid markers) has rendered
// before we look for the first invalid field.
//
// Dependency-free and SSR-safe.

/** Find the nearest focusable control to focus for a given invalid element. If the element
 *  itself is focusable we use it; otherwise the first focusable input/select/textarea/button
 *  inside it; otherwise the element itself (which gets a tabindex). */
function focusTarget(el: HTMLElement): HTMLElement {
  const focusableSelector = "input, select, textarea, button, [href], [tabindex]";
  if (el.matches(focusableSelector)) return el;
  const inner = el.querySelector<HTMLElement>("input, select, textarea, button");
  if (inner) return inner;
  if (!el.hasAttribute("tabindex")) el.setAttribute("tabindex", "-1");
  return el;
}

/**
 * Scroll + focus the first invalid field inside `container`. Looks for `[aria-invalid="true"]`
 * first (DOM order), then falls back to `[data-invalid="true"]`. Returns whether it focused
 * something. No-op (returns false) when there is no container or no DOM (SSR).
 */
export function focusFirstError(container: HTMLElement | null): boolean {
  if (typeof window === "undefined" || !container) return false;

  // Whether an invalid field is present now. The actual scroll + focus happens in the rAF
  // (after the error UI has rendered), but the markers are already in the DOM by the time a
  // failed submit calls this, so we can report synchronously.
  const hasError =
    !!container.querySelector('[aria-invalid="true"]') ||
    !!container.querySelector('[data-invalid="true"]');

  window.requestAnimationFrame(() => {
    const invalid =
      container.querySelector<HTMLElement>('[aria-invalid="true"]') ??
      container.querySelector<HTMLElement>('[data-invalid="true"]');
    if (!invalid) return;
    invalid.scrollIntoView({ block: "center", behavior: "smooth" });
    focusTarget(invalid).focus({ preventScroll: true });
  });

  return hasError;
}
